import React, { CSSProperties, useState, useRef, useEffect } from 'react';
import styles from './mini-input.module.scss';
import { RoundTriangle } from '../triangle/round-triangle';
import { PatternFormat } from 'react-number-format';
import { normalizePhone } from 'shared/lib';

interface IMiniInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  inputValue?: string;
  inputTitle?: string;
  inputPlaceholder?: string;
  inputType?: string;
  style?: CSSProperties;
  options?: Array<{ category?: string; name?: string } | string>;
  isPhone?: boolean;
  onChange?: (value: any) => void;
  openUpward?: boolean;
}

export const MiniInput: React.FC<IMiniInputProps> = ({
  inputValue,
  style,
  inputTitle,
  inputPlaceholder = 'Пример',
  inputType = 'text',
  options = null,
  onChange,
  isPhone = false,
  openUpward = false,
  className,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleChange = (
    value: string | { category?: string; name?: string }
  ) => {
    if (onChange) {
      if (typeof value === 'string') {
        onChange(value);
      } else {
        onChange(value);
      }
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const renderOption = (
    option: { category?: string; name?: string } | string
  ) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.category || option.name || JSON.stringify(option);
  };
  const handlePhoneChange = (values: { value: string }) => {
    if (onChange) {
      const normalized = normalizePhone(values.value);
      onChange(normalized);
    }
    setIsOpen(false);
  };

  const handlePhonePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text');
    const digits = pasted.replace(/\D/g, '');
    if (digits.length > 10 && onChange) {
      e.preventDefault();
      onChange(normalizePhone(digits));
    }
  };

  const renderInput = () => {
    if (options) {
      return (
        <div
          style={style}
          className={`${styles.custom_select} ${className || ''} ${openUpward ? styles.custom_select_upward : ''}`}
          ref={selectRef}
        >
          <div
            className={styles.select__header}
            onClick={() => setIsOpen(!isOpen)}
          >
            {inputValue || (
              <div className={styles.select__placeholder}>
                {inputPlaceholder}
              </div>
            )}
            <div className={styles.triangle_wrapper}>
              <RoundTriangle isRotated={isOpen} />
            </div>
          </div>
          <div
            className={`${styles.select__options} ${
              isOpen ? styles.select__options_open : ''
            }`}
          >
            <div className={styles.options__container}>
              {options.map((option, index) => (
                <div
                  key={index}
                  className={styles.options__option}
                  onClick={() => handleChange(option)}
                >
                  {renderOption(option)}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else if (isPhone) {
      const displayValue = inputValue?.startsWith('7')
        ? inputValue.slice(1)
        : inputValue;

      return (
        <PatternFormat
          id="phone"
          name="phone"
          format="+7 (###) ###-##-##"
          mask="_"
          className={`${styles.mini_input__input} ${className || ''}`}
          data-cy="phone"
          placeholder="+7 (999) 999-99-99"
          allowEmptyFormatting
          onValueChange={handlePhoneChange}
          onPaste={handlePhonePaste}
          value={displayValue}
          style={style}
        />
      );
    }
    return (
      <input
        value={inputValue}
        className={`${styles.mini_input__input} ${className || ''}`}
        type={inputType}
        placeholder={inputPlaceholder}
        onChange={(e) => onChange && onChange(e.target.value)}
        style={style}
        {...props}
      />
    );
  };

  return (
    <div className={styles.mini_input}>
      {inputTitle && (
        <div className={styles.mini_input__title}>{inputTitle}</div>
      )}
      {renderInput()}
    </div>
  );
};
